import React from 'react';
import {
  Flex,
  Card,
  CardContent,
  Heading,
  Text,
  Button,
  Divider,
  TextField,
  Checkbox,
  Spinner,
  Banner,
} from '@chatsdet/ui';
import { Link, useSearch } from '@tanstack/react-router';
import { Copy, Check } from '@phosphor-icons/react';
import { ApiError } from '../../lib/api-error';
import {
  useMfaChallengeEnrollStart,
  useMfaChallengeEnrollConfirm,
  useMfaChallengeVerify,
} from '../../hooks/useMfa';
import { copyTextToClipboard } from '../components/settings/clipboard-copy';
import { BRAND } from '../../brand';
import type { MfaChallengeSearchParams } from '../../features/auth/routes';

/**
 * `/login/mfa` — reached with NO session yet, only the short-lived, HttpOnly
 * `mfa_challenge` cookie set by `/auth/email/signin` (or an OAuth redirect).
 * `?mode=enroll` walks a first-time authenticator setup; `?mode=verify` asks
 * for a code (or recovery code) from an already-enrolled factor. Either path
 * ends by handing the browser real session cookies and a full reload to the
 * validated `returnTo` (see `validateMfaChallengeSearch`) or `/`.
 */
export function MfaChallengePage() {
  const search = useSearch({ strict: false }) as MfaChallengeSearchParams;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col items-center justify-center p-6">
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

      <Card variant="bordered" className="relative max-w-md w-full shadow-2xl">
        <CardContent className="p-8 space-y-8">
          <div className="text-center space-y-3">
            <Heading level={1} className="text-2xl font-bold text-foreground">
              {search.mode === 'enroll' ? 'Set up two-factor authentication' : 'Two-factor authentication'}
            </Heading>
            <Text className="text-muted-foreground">
              {search.mode === 'enroll'
                ? `${BRAND.name} requires an authenticator app for your account.`
                : 'Enter the code from your authenticator app to finish signing in.'}
            </Text>
          </div>

          <Divider />

          {search.mode === 'enroll' ? <EnrollView returnTo={search.returnTo} /> : <VerifyView returnTo={search.returnTo} />}
        </CardContent>
      </Card>
    </div>
  );
}

function challengeExpiredMessage(error: unknown): string | null {
  if (error instanceof ApiError && error.code === 'MFA_CHALLENGE_EXPIRED') {
    return 'Your sign-in attempt timed out. Please sign in again.';
  }
  return null;
}

function genericErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return fallback;
}

function ChallengeExpiredBanner() {
  return (
    <div className="space-y-4">
      <Banner variant="error">Your sign-in attempt timed out. Please sign in again.</Banner>
      <Link to="/login" className="block text-center text-sm text-primary hover:underline">
        Back to sign in
      </Link>
    </div>
  );
}

function VerifyView({ returnTo }: { returnTo?: string }) {
  const [code, setCode] = React.useState('');
  const [recoveryCode, setRecoveryCode] = React.useState('');
  const [useRecovery, setUseRecovery] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expired, setExpired] = React.useState(false);

  const verifyMutation = useMfaChallengeVerify();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const params = useRecovery ? { recoveryCode: recoveryCode.trim() } : { code };

    verifyMutation.mutate(params, {
      onSuccess: () => {
        // Session cookies were just set server-side; full reload, matching
        // LoginPage's post-signin redirect.
        window.location.href = returnTo ?? '/';
      },
      onError: (err) => {
        const expiredMessage = challengeExpiredMessage(err);
        if (expiredMessage) {
          setExpired(true);
          return;
        }
        setError(genericErrorMessage(err, 'That code did not work. Please try again.'));
      },
    });
  };

  if (expired) {
    return <ChallengeExpiredBanner />;
  }

  const canSubmit = useRecovery ? recoveryCode.trim().length > 0 : code.length === 6;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <Text size="sm" className="text-destructive">
            {error}
          </Text>
        </div>
      )}

      {!useRecovery ? (
        <TextField
          label="Authentication code"
          value={code}
          onChange={(val) => setCode(val.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          inputMode="numeric"
          autoComplete="one-time-code"
          className="text-center text-2xl"
          autoFocus
        />
      ) : (
        <TextField
          label="Recovery code"
          value={recoveryCode}
          onChange={setRecoveryCode}
          placeholder="xxxxx-xxxxx"
          autoComplete="off"
          autoFocus
        />
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        isDisabled={verifyMutation.isPending || !canSubmit}
      >
        {verifyMutation.isPending ? (
          <Flex align="center" gap="sm">
            <Spinner size="sm" />
            <span>Verifying...</span>
          </Flex>
        ) : (
          'Verify'
        )}
      </Button>

      <div className="text-center">
        <button
          type="button"
          className="text-sm text-primary hover:underline"
          onClick={() => {
            setUseRecovery((prev) => !prev);
            setError(null);
          }}
        >
          {useRecovery ? 'Use an authenticator code instead' : 'Use a recovery code instead'}
        </button>
      </div>

      <div className="text-center">
        <Link to="/login" className="text-sm text-muted-foreground hover:underline">
          Back to sign in
        </Link>
      </div>
    </form>
  );
}

function EnrollView({ returnTo }: { returnTo?: string }) {
  const startMutation = useMfaChallengeEnrollStart();
  const confirmMutation = useMfaChallengeEnrollConfirm();

  const [code, setCode] = React.useState('');
  const [confirmError, setConfirmError] = React.useState<string | null>(null);
  const [expired, setExpired] = React.useState(false);
  const [recoveryCodes, setRecoveryCodes] = React.useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = React.useState(false);

  const startMutate = startMutation.mutate;
  React.useEffect(() => {
    startMutate(undefined, {
      onError: (err) => {
        if (challengeExpiredMessage(err)) setExpired(true);
      },
    });
    // Fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setConfirmError(null);
    confirmMutation.mutate(code, {
      onSuccess: (data) => {
        setRecoveryCodes(data.recoveryCodes);
      },
      onError: (err) => {
        if (challengeExpiredMessage(err)) {
          setExpired(true);
          return;
        }
        setConfirmError(genericErrorMessage(err, 'That code did not work. Please try again.'));
      },
    });
  };

  if (expired) {
    return <ChallengeExpiredBanner />;
  }

  if (recoveryCodes) {
    return (
      <RecoveryCodesPanel
        codes={recoveryCodes}
        acknowledged={acknowledged}
        setAcknowledged={setAcknowledged}
        onContinue={() => {
          window.location.href = returnTo ?? '/';
        }}
      />
    );
  }

  if (startMutation.isError) {
    return (
      <div className="space-y-4 text-center">
        <Text size="sm" className="text-destructive">
          {genericErrorMessage(startMutation.error, 'Failed to start MFA setup.')}
        </Text>
        <Button variant="outline" onPress={() => startMutation.mutate(undefined)}>
          Try again
        </Button>
      </div>
    );
  }

  if (startMutation.isPending || !startMutation.data) {
    return (
      <Flex align="center" justify="center" className="py-8">
        <Spinner size="lg" />
      </Flex>
    );
  }

  const { qrSvgDataUri, secret } = startMutation.data;

  return (
    <div className="space-y-6">
      <div className="space-y-3 text-center">
        <Text size="sm" className="text-muted-foreground">
          Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy).
        </Text>
        <div className="flex justify-center">
          <img
            src={qrSvgDataUri}
            alt="Authenticator app QR code"
            className="h-44 w-44 rounded-md border border-border bg-white p-2"
          />
        </div>
        <div className="space-y-1">
          <Text size="xs" className="text-muted-foreground">
            Can&apos;t scan? Enter this key manually:
          </Text>
          <code
            className="block select-all break-all rounded-md bg-muted px-3 py-2 text-xs font-mono"
            aria-label="Authenticator setup key"
          >
            {secret}
          </code>
        </div>
      </div>

      <form onSubmit={handleConfirm} className="space-y-4">
        {confirmError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <Text size="sm" className="text-destructive">
              {confirmError}
            </Text>
          </div>
        )}

        <TextField
          label="Enter the 6-digit code from your app"
          value={code}
          onChange={(val) => setCode(val.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          inputMode="numeric"
          autoComplete="one-time-code"
          className="text-center text-2xl"
          autoFocus
        />

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          isDisabled={confirmMutation.isPending || code.length !== 6}
        >
          {confirmMutation.isPending ? (
            <Flex align="center" gap="sm">
              <Spinner size="sm" />
              <span>Confirming...</span>
            </Flex>
          ) : (
            'Confirm & continue'
          )}
        </Button>
      </form>
    </div>
  );
}

function RecoveryCodesPanel({
  codes,
  acknowledged,
  setAcknowledged,
  onContinue,
}: {
  codes: string[];
  acknowledged: boolean;
  setAcknowledged: (v: boolean) => void;
  onContinue: () => void;
}) {
  const [copied, setCopied] = React.useState(false);

  return (
    <div className="space-y-4">
      <Banner variant="warning">
        Save these recovery codes now. They are shown only once and cannot be retrieved again.
      </Banner>

      <div className="rounded-md border border-border bg-muted p-4">
        <div className="grid grid-cols-2 gap-2 font-mono text-sm">
          {codes.map((c) => (
            <div key={c} className="select-all">
              {c}
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="outline"
        className="w-full"
        iconLeft={copied ? <Check size={16} /> : <Copy size={16} />}
        onPress={async () => {
          const ok = await copyTextToClipboard(codes.join('\n'));
          if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }
        }}
      >
        {copied ? 'Copied' : 'Copy all codes'}
      </Button>

      <Checkbox
        isSelected={acknowledged}
        onChange={setAcknowledged}
        label="I've saved these recovery codes in a safe place"
      />

      <Button
        variant="primary"
        size="lg"
        className="w-full"
        isDisabled={!acknowledged}
        onPress={onContinue}
      >
        Continue
      </Button>
    </div>
  );
}
